/**
 * The frame every screen sits in: a sidebar, a breadcrumb strip on the frame, and a single
 * rounded panel that is the only scrolling region.
 *
 * Below 820px the sidebar becomes a drawer with a trigger in the breadcrumb strip. The prototype
 * simply hides it and offers nothing.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation } from 'react-router';
import {
  Inbox,
  GitMerge,
  BookText,
  LayoutGrid,
  Scale,
  Sun,
  Moon,
  Menu,
  AlertTriangle,
  History,
  Settings,
  CircleHelp,
  ExternalLink,
} from 'lucide-react';
import { useTheme } from '~/hooks/useTheme';
import { Fragment } from 'react';
import { useQueue, useProposals, useContradictions } from '~/api/queries';
import { useAuth } from '~/auth/AuthProvider';
import { CountBadge } from '~/ui/Pill';
import { IconButton } from '~/ui/Button';
import { routes } from '~/lib/routes';
import { plural } from '~/lib/format';
import { cx } from '~/lib/cx';
import styles from './AppShell.module.css';

interface NavEntry {
  to: string;
  label: string;
  icon: ReactNode;
  count?: number;
  /** A route whose prefix keeps this item lit — the detail page is a child of the list. */
  match: string;
  /** Shown indented under the entry while its section is open. */
  children?: readonly NavEntry[];
  /**
   * Leaves the application. Rendered as a real anchor, not a `NavLink`: the help portal is a
   * separate static site served at the same origin, and client-side routing to it would be
   * handled by this router, which has no such page.
   */
  external?: boolean;
}

function useNavEntries(): readonly NavEntry[] {
  // Unscoped: the counts are across every matter, because the workspace is one platform and
  // each screen filters for itself. A badge narrowed by a control the user cannot see is a
  // number nobody can explain.
  const queue = useQueue();
  const proposals = useProposals();
  const contradictions = useContradictions({});

  return [
    {
      to: routes.jurisdictions(),
      label: 'Jurisdictions',
      icon: <Scale aria-hidden="true" />,
      match: '/jurisdictions',
    },
    {
      to: routes.ruleCatalogue(),
      label: 'Rules',
      icon: <BookText aria-hidden="true" />,
      match: '/rules',
    },
    {
      to: routes.coverage(),
      label: 'Coverage',
      icon: <LayoutGrid aria-hidden="true" />,
      match: '/coverage',
    },
    {
      to: routes.queue(),
      label: 'Queue',
      icon: <Inbox aria-hidden="true" />,
      ...(queue.data?.open ? { count: queue.data.open } : {}),
      match: '/queue',
    },
    {
      to: routes.review(),
      label: 'AI research review',
      icon: <GitMerge aria-hidden="true" />,
      ...(proposals.data?.proposals.length
        ? { count: proposals.data.proposals.length }
        : {}),
      match: '/review',
    },
    {
      to: routes.contradictions(),
      label: 'Contradictions',
      icon: <AlertTriangle aria-hidden="true" />,
      ...(contradictions.data?.total ? { count: contradictions.data.total } : {}),
      match: '/contradictions',
    },
    {
      to: routes.runs(),
      label: 'Run history',
      icon: <History aria-hidden="true" />,
      match: '/runs',
    },
    {
      to: routes.admin(),
      label: 'Admin',
      icon: <Settings aria-hidden="true" />,
      match: '/admin',
      // Every admin route, in the order somebody sets a deployment up: who may work, what they
      // may work on, where rules come from, and the corpus itself. A route reachable only by
      // typing its URL is one nobody finds.
      children: [
        { to: routes.adminPeople(), label: 'People', icon: null, match: '/admin/people' },
        { to: routes.adminRegisters(), label: 'Registers', icon: null, match: '/admin/registers' },
        { to: routes.adminSources(), label: 'Official sources', icon: null, match: '/admin/sources' },
        { to: routes.prompts(), label: 'AI prompts', icon: null, match: '/prompts' },
        { to: routes.adminInterchange(), label: 'Import', icon: null, match: '/admin/interchange' },
      ],
    },
    {
      to: routes.help(),
      label: 'Help',
      icon: <CircleHelp aria-hidden="true" />,
      match: '/help',
      external: true,
    },
  ];
}

function SidebarBody({ onNavigate }: { onNavigate?: () => void }) {
  const entries = useNavEntries();
  const { pathname } = useLocation();
  const { theme, toggle } = useTheme();
  const { user } = useAuth();

  return (
    <>
      <div className={styles.logo}>
        <span className={styles.logoTile} aria-hidden="true">
          <Scale />
        </span>
        The Law
      </div>
      <nav className={styles.nav} aria-label="Sections">
        {entries.map((entry) => {
          const current = pathname === entry.match || pathname.startsWith(`${entry.match}/`);
          const label =
            entry.count === undefined
              ? entry.label
              : `${entry.label}, ${plural(entry.count, 'open item')}`;
          // A section opens when you are anywhere inside it, including on one of its own pages,
          // so the three sit under Admin rather than replacing the place you came from.
          const open =
            entry.children !== undefined &&
            (current || entry.children.some((child) => pathname.startsWith(child.match)));
          if (entry.external) {
            return (
              <a
                key={entry.to}
                href={entry.to}
                className={cx(styles.navItem)}
                target="_blank"
                rel="noreferrer"
                // The icon and the visible label are both hidden from the accessibility tree, as
                // in the pattern below, so the name comes from here — and it says where the link
                // goes, because a new tab with no warning is disorienting with a screen reader.
                aria-label={`${entry.label}, opens in a new tab`}
                onClick={onNavigate}
              >
                <span className={styles.navBadge} aria-hidden="true">
                  {entry.icon}
                </span>
                <span aria-hidden="true">{entry.label}</span>
                <ExternalLink className={styles.navExternal} aria-hidden="true" />
              </a>
            );
          }
          return (
            <Fragment key={entry.to}>
              <NavLink
                to={entry.to}
                className={cx(styles.navItem)}
                aria-current={current ? 'page' : undefined}
                aria-label={label}
                onClick={onNavigate}
              >
                <span className={styles.navBadge} aria-hidden="true">
                  {entry.icon}
                </span>
                <span aria-hidden="true">{entry.label}</span>
                {entry.count ? <CountBadge count={entry.count} active={current} /> : null}
              </NavLink>
              {open
                ? entry.children?.map((child) => (
                    <NavLink
                      key={child.to}
                      to={child.to}
                      className={cx(styles.navItem, styles.navChild)}
                      aria-current={pathname.startsWith(child.match) ? 'page' : undefined}
                      // The label is hidden from the accessibility tree along with the icon in
                      // the parent pattern, which leaves a link with no discernible name. The
                      // parent gets its name from `aria-label`; so does this.
                      aria-label={child.label}
                      onClick={onNavigate}
                    >
                      <span aria-hidden="true">{child.label}</span>
                    </NavLink>
                  ))
                : null}
            </Fragment>
          );
        })}
      </nav>
      <div className={styles.grow} />
      <div className={styles.foot}>
        <p className={styles.user}>{user?.displayName ?? 'Not signed in'}</p>
        <div className={styles.footRow}>
          <IconButton
            icon={theme === 'dark' ? <Sun aria-hidden="true" /> : <Moon aria-hidden="true" />}
            aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
            onClick={toggle}
          />
        </div>
      </div>
    </>
  );
}

export interface Crumb {
  label: string;
  href?: string;
}

export interface AppShellProps {
  // `| undefined` on each optional, because these are forwarded from a caller that may hold the
  // value as undefined rather than omit it, and `exactOptionalPropertyTypes` tells those apart.
  trail?: readonly Crumb[] | undefined;
  leaf: string;
  count?: number | undefined;
  width?: 'wide' | 'reading' | undefined;
  children: ReactNode;
}

export function AppShell({ trail = [], leaf, count, width = 'wide', children }: AppShellProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();

  // Scroll resets on a push; the browser restores it on a pop. A scroll position is not worth
  // an exception, so a container without scrollTo simply keeps its position.
  useEffect(() => {
    scrollRef.current?.scrollTo?.({ top: 0 });
  }, [pathname]);

  return (
    <div className={styles.shell}>
      <aside className={styles.sidebar}>
        <SidebarBody />
      </aside>

      {drawerOpen ? (
        <>
          <div
            className={styles.drawerBackdrop}
            onMouseDown={() => setDrawerOpen(false)}
            role="presentation"
          />
          <div className={styles.drawer} role="dialog" aria-label="Sections">
            <SidebarBody onNavigate={() => setDrawerOpen(false)} />
          </div>
        </>
      ) : null}

      <div className={styles.mainwrap}>
        <div className={styles.surround}>
          <IconButton
            icon={<Menu aria-hidden="true" />}
            aria-label="Open the section menu"
            className={styles.drawerTrigger}
            onClick={() => setDrawerOpen(true)}
          />
          <nav aria-label="Breadcrumb" className={styles.crumbNav}>
            <ol className={styles.crumbList}>
              <li className={styles.crumbTile} aria-hidden="true">
                <Scale />
              </li>
              <li>
                <Link to={routes.home()} className={styles.crumbRoot}>
                  The Law
                </Link>
              </li>
              {trail.map((crumb) => (
                <li key={crumb.label}>
                  <span className={styles.crumbSep} aria-hidden="true">
                    /
                  </span>{' '}
                  {crumb.href ? (
                    <Link to={crumb.href} className={styles.crumbLink}>
                      {crumb.label}
                    </Link>
                  ) : (
                    <span className={styles.crumbLink}>{crumb.label}</span>
                  )}
                </li>
              ))}
              <li>
                <span className={styles.crumbSep} aria-hidden="true">
                  /
                </span>{' '}
                <span className={styles.crumbLeaf} aria-current="page">
                  {leaf}
                </span>
                {count === undefined ? null : (
                  <span className={styles.crumbCount}>{count}</span>
                )}
              </li>
            </ol>
          </nav>
          <span className={styles.spacer} />
        </div>

        <main className={styles.panel}>
          <div className={styles.panelScroll} ref={scrollRef}>
            <div className={cx(styles.wrap, width === 'reading' && styles.reading)}>
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export function PageHeader({
  title,
  description,
  actions,
  headingRef,
}: {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
  headingRef?: React.Ref<HTMLHeadingElement>;
}) {
  return (
    <div className={styles.pageHead}>
      <div>
        <h1 ref={headingRef} tabIndex={-1}>
          {title}
        </h1>
        {description ? <p className={styles.pageSub}>{description}</p> : null}
      </div>
      <span className={styles.spacer} />
      {actions}
    </div>
  );
}

export function Toolbar({ children }: { children: ReactNode }) {
  return <div className={styles.toolbar}>{children}</div>;
}

export function MasterDetailLayout({ rail, children }: { rail: ReactNode; children: ReactNode }) {
  return (
    <div className={styles.masterDetail}>
      {rail}
      <div>{children}</div>
    </div>
  );
}

export function Rail({ children, label }: { children: ReactNode; label: string }) {
  return (
    <nav aria-label={label}>
      <ul className={styles.rail}>{children}</ul>
    </nav>
  );
}

export function RailItem({
  to,
  name,
  meta,
  tag,
  current,
}: {
  to: string;
  name: string;
  meta: string;
  tag?: string;
  current: boolean;
}) {
  return (
    <li>
      <NavLink
        to={to}
        className={cx(styles.railItem)}
        aria-current={current ? 'page' : undefined}
        aria-label={`${name} — ${meta}`}
      >
        <span className={styles.railName}>{name}</span>
        <span className={styles.railMeta}>{meta}</span>
        {tag ? <span className={styles.railTag}>{tag}</span> : null}
      </NavLink>
    </li>
  );
}

export function DetailPaneHeader({
  title,
  meta,
  actions,
  headingRef,
}: {
  title: string;
  meta?: ReactNode;
  actions?: ReactNode;
  headingRef?: React.Ref<HTMLHeadingElement>;
}) {
  return (
    <div className={styles.paneHead}>
      <div>
        <h2 ref={headingRef} tabIndex={-1}>
          {title}
        </h2>
        {meta ? <p className={styles.paneMeta}>{meta}</p> : null}
      </div>
      <span className={styles.spacer} />
      {actions}
    </div>
  );
}
